/**
 * Section Report header: pipe/section spec chips and completed-welds KPI.
 *
 * Data sources (SOW from GET /sows):
 * - Pipe # ............... pipeStringId
 * - Pipe name ............ pipelineSection
 * - OD ................... outerDiameterInch (inch)
 * - WT ................... wallThicknessMm (mm)
 * - Length ............... expectedPipeLength, else planned qty × pipeLength
 * - Welds (planned) ...... plannedWelds / plannedWorkItems rollup
 * - Completed welds ...... actualWelds on SOW; optional dashboard pipeTally.totalCompleted
 */

import { displayPlannedQtySummary } from './plannedWorkItems';
import { displayExpectedPipeLength } from './sowExpectedLength';

const EM_DASH = '—';

export const formatHeaderValue = (value) => {
  if (value == null) return EM_DASH;
  const text = String(value).trim();
  return text || EM_DASH;
};

export const readSowField = (record, camel, snake) =>
  record == null ? undefined : record[camel] ?? record[snake];

export const readSowNumberDisplay = (record, camel, snake) => {
  const v = readSowField(record, camel, snake);
  if (v == null || v === '') return EM_DASH;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (Number.isNaN(n)) return EM_DASH;
  return String(n);
};

/** Same as project dashboard: `Project : PIH-1254 (1254)` */
export function formatProjectDetailTitle(project) {
  const name = project?.projectName ?? project?.name;
  const id = project?.projectId ?? project?.id;
  if (name != null && String(name).trim() !== '' && id != null && String(id).trim() !== '') {
    return `Project : ${String(name).trim()} (${String(id).trim()})`;
  }
  if (name != null && String(name).trim() !== '') {
    return `Project : ${String(name).trim()}`;
  }
  return 'Project';
}

export function getSectionReportHeaderSpec(pipeline, project) {
  const sow = pipeline ?? {};
  return {
    projectTitle: formatProjectDetailTitle(project),
    projectName: formatHeaderValue(project?.projectName ?? project?.name),
    pipeNumber: formatHeaderValue(sow.pipeStringId),
    pipeName: formatHeaderValue(sow.pipelineSection),
    od: readSowNumberDisplay(sow, 'outerDiameterInch', 'outer_diameter_inch'),
    odUnit: 'in',
    wt: readSowNumberDisplay(sow, 'wallThicknessMm', 'wall_thickness_mm'),
    wtUnit: 'mm',
    length: formatHeaderValue(displayExpectedPipeLength(sow)),
    weldsPlanned: formatHeaderValue(displayPlannedQtySummary(sow)),
    completedWelds: readSowNumberDisplay(sow, 'actualWelds', 'actual_welds'),
  };
}

/** Readable single-line spec: Pipe # · Name · OD · WT · Length · Welds */
export function formatSectionReportSpecLine(spec) {
  const s = spec ?? {};
  const parts = [
    s.pipeNumber !== EM_DASH ? `Pipe # ${s.pipeNumber}` : null,
    s.pipeName !== EM_DASH ? s.pipeName : null,
    s.od !== EM_DASH ? `OD ${s.od} ${s.odUnit}` : null,
    s.wt !== EM_DASH ? `WT ${s.wt} ${s.wtUnit}` : null,
    s.length !== EM_DASH ? `Length ${s.length}` : null,
    s.weldsPlanned !== EM_DASH ? `Welds ${s.weldsPlanned}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : EM_DASH;
}

/** KPI value: prefer live dashboard tally when provided, else SOW actualWelds. */
export function resolveCompletedWeldsKpi(spec, dashboardPipeTally) {
  const fromDashboard = dashboardPipeTally?.totalCompleted;
  if (fromDashboard != null && fromDashboard !== '' && !Number.isNaN(Number(fromDashboard))) {
    return String(fromDashboard);
  }
  const fromSow = spec?.completedWelds;
  return fromSow !== EM_DASH ? fromSow : EM_DASH;
}

/** Chip list for the Section Report title row. */
export function getSectionReportSummaryChips(spec, completedWeldsKpi) {
  const s = spec ?? {};
  const chips = [];
  if (s.projectName !== EM_DASH) {
    chips.push({ key: 'project', label: 'Project', value: s.projectName });
  }
  if (s.pipeNumber !== EM_DASH) chips.push({ key: 'pipeNumber', label: 'Pipe #', value: s.pipeNumber });
  if (s.pipeName !== EM_DASH) chips.push({ key: 'pipeName', label: 'Mainline', value: s.pipeName });
  if (s.od !== EM_DASH) chips.push({ key: 'od', label: 'OD', value: `${s.od} ${s.odUnit}` });
  if (s.wt !== EM_DASH) chips.push({ key: 'wt', label: 'WT', value: `${s.wt} ${s.wtUnit}` });
  if (s.length !== EM_DASH) chips.push({ key: 'length', label: 'Length', value: s.length });
  if (s.weldsPlanned !== EM_DASH) chips.push({ key: 'welds', label: 'Welds', value: s.weldsPlanned });
  if (completedWeldsKpi != null && completedWeldsKpi !== '' && completedWeldsKpi !== EM_DASH) {
    chips.push({
      key: 'completedWelds',
      label: 'Completed welds',
      value: completedWeldsKpi,
      highlight: true,
    });
  }
  return chips;
}
